import * as React from "react";
import { Asterisk, ChevronsUpDown, Plus } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { AccountModel, useAuth } from "../utils/AuthContext.tsx";
import { Avatar, AvatarImage } from "./ui/avatar.tsx";

export interface AccountSwitcherProps {
  onSuccess: (account: AccountModel) => void;
  onFailure: (account: AccountModel) => void;
  onAdd: () => void;
  side: "bottom" | "right";
}

export const AccountSwitcher: React.FC<AccountSwitcherProps> = ({ onSuccess, onFailure, onAdd, side }) => {
  const { userAccounts, currentAccount, switchToAccount, logout } = useAuth();

  const handleSwitchToAccount = (account: AccountModel) => {
    try {
      switchToAccount(account);
      onSuccess(account);
    } catch (err) {
      console.error(`Error switching to account ${account.id}:`, err);
      console.warn("Forcing logout.");
      //switching failed most likely to missing token - send to account login page
      logout(account);
      onFailure(account);
    }
  };

  if (!currentAccount) {
    return null;
  }
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-md">
                <AvatarImage src={currentAccount.logo} />
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight pl-1">
                <span className="truncate font-semibold">{currentAccount.name}</span>
                {/*<span className="truncate text-xs">{currentAccount.id}</span>*/}
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-64 rounded-lg"
            align="start"
            side={side}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">Accounts</DropdownMenuLabel>
            {userAccounts.map((a, index) => (
              <DropdownMenuItem key={index} onClick={() => handleSwitchToAccount(a)} className="gap-3 p-2">
                <Avatar className="h-6 w-6 rounded-sm">
                  <AvatarImage src={a.logo} />
                </Avatar>
                {a.name}
                {a.id == currentAccount.id && (
                  <DropdownMenuShortcut>
                    <Asterisk className="w-4 h-4" />
                  </DropdownMenuShortcut>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 p-2" onClick={onAdd}>
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">Add account</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
